import { useEffect, useMemo, useRef, useState } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Popover from "@mui/material/Popover";
import Select from "@mui/material/Select";
import Slider from "@mui/material/Slider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import * as maplibregl from "maplibre-gl";
import { type GeoJSONSource, type Map as MapLibre } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import "./worker";
import { api, unwrap, type TimelineLane } from "../api/client";
import type { FeatureCollection } from "geojson";
import { COARSE } from "../theme";
import { mapBounds, offlineStyle, type Bounds, type MapSet } from "./style";
import {
  applyOverlays,
  loadOverlayState,
  move,
  ordered,
  saveOverlayState,
  type OverlayState,
} from "./overlays";
import {
  insideIntervals,
  nearestFix,
  heatFeatures,
  placeFeatures,
  waypointFeatures,
  positionFeatures,
  sectionFeatures,
  SOURCE_COLOURS,
  spanFeatures,
  trackBounds,
  type Interval,
  type SourceTrack,
} from "./tracks";

const MAP_KEY = "avgv.mapSet";
const START = "#2e7d32";
const END = "#c62828";
const EMPTY: FeatureCollection = { type: "FeatureCollection", features: [] };

function storedMap(): string | null {
  try {
    return localStorage.getItem(MAP_KEY);
  } catch {
    return null;
  }
}

function covers(m: MapSet, b: Bounds): boolean {
  const [w, s, e, n] = mapBounds(m);
  return w <= b[0] && s <= b[1] && e >= b[2] && n >= b[3];
}

function dwell(ms: number): string {
  const m = Math.round(ms / 60_000);
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${m % 60} min`;
}

/** Adds the track layers; a style change removes them, so this runs after each. */
function addOverlays(map: MapLibre) {
  map.addSource("area", { type: "geojson", data: EMPTY });
  map.addSource("span", { type: "geojson", data: EMPTY });
  map.addSource("sections", { type: "geojson", data: EMPTY });
  map.addSource("places", { type: "geojson", data: EMPTY });
  map.addSource("positions", { type: "geojson", data: EMPTY });
  map.addSource("heat", { type: "geojson", data: EMPTY });
  map.addSource("waypoints", { type: "geojson", data: EMPTY });
  map.addLayer({
    id: "heat",
    type: "heatmap",
    source: "heat",
    layout: { visibility: "none" },
    paint: {
      "heatmap-radius": ["interpolate", ["linear"], ["zoom"], 8, 6, 16, 24],
      "heatmap-intensity": ["interpolate", ["linear"], ["zoom"], 8, 0.6, 16, 2],
      "heatmap-opacity": 0.75,
    },
  });
  map.addLayer({
    id: "sections-still",
    type: "line",
    source: "sections",
    filter: ["!=", ["get", "kind"], "moving"],
    layout: { "line-cap": "round", "line-join": "round" },
    paint: {
      "line-color": ["get", "colour"],
      "line-width": 3,
      "line-opacity": 0.5,
    },
  });
  map.addLayer({
    id: "sections-moving",
    type: "line",
    source: "sections",
    filter: ["==", ["get", "kind"], "moving"],
    layout: { "line-cap": "round", "line-join": "round" },
    paint: { "line-color": ["get", "colour"], "line-width": 4 },
  });
  map.addLayer({
    id: "span",
    type: "line",
    source: "span",
    layout: { "line-cap": "round", "line-join": "round" },
    paint: { "line-color": ["get", "colour"], "line-width": 7 },
  });
  map.addLayer({
    id: "area",
    type: "fill",
    source: "area",
    paint: {
      "fill-color": "#ff9800",
      "fill-opacity": 0.12,
      "fill-outline-color": "#ff9800",
    },
  });
  map.addLayer({
    id: "places",
    type: "circle",
    source: "places",
    paint: {
      "circle-radius": ["match", ["get", "kind"], "stop", 9, 7],
      "circle-color": [
        "match",
        ["get", "kind"],
        "track_start",
        START,
        "track_end",
        END,
        ["get", "colour"],
      ],
      "circle-stroke-color": "#ffffff",
      "circle-stroke-width": 2,
    },
  });
  map.addLayer({
    id: "place-labels",
    type: "symbol",
    source: "places",
    filter: ["==", ["get", "kind"], "stop"],
    layout: {
      "text-field": ["get", "label"],
      "text-font": ["Noto Sans Medium"],
      "text-size": 11,
      "text-allow-overlap": true,
    },
    paint: { "text-color": "#ffffff" },
  });
  map.addLayer({
    id: "waypoints",
    type: "circle",
    source: "waypoints",
    paint: {
      "circle-radius": 5,
      "circle-color": "#37474f",
      "circle-stroke-color": "#ffffff",
      "circle-stroke-width": 2,
    },
  });
  map.addLayer({
    id: "waypoint-labels",
    type: "symbol",
    source: "waypoints",
    layout: {
      "text-field": ["get", "name"],
      "text-font": ["Noto Sans Medium"],
      "text-size": 11,
      "text-offset": [0, 1.2],
      "text-anchor": "top",
    },
    paint: {
      "text-color": "#263238",
      "text-halo-color": "#ffffff",
      "text-halo-width": 1.5,
    },
  });
  map.addLayer({
    id: "positions",
    type: "circle",
    source: "positions",
    paint: {
      "circle-radius": 8,
      "circle-color": ["get", "colour"],
      "circle-stroke-color": "#ffffff",
      "circle-stroke-width": 3,
    },
  });
}

/**
 * Location tracks of the day shown, on an offline base map (design §9):
 * sections coloured by source (paler where not moving), start, end and
 * numbered stops, and each source's position at the playhead. Clicking a
 * place or a track plays from its time. `span` (a time range brushed on the
 * timeline) is highlighted; a drawn `area` reports when each GPS source was
 * inside it.
 */
export function MapView(props: {
  lanes: readonly TimelineLane[];
  playhead: number | null;
  onSeek: (utcMs: number) => void;
  span: Interval | null;
  area: Area | null;
  onArea: (area: Area | null) => void;
  /** Per GPS source (`library/source`), when it was inside `area`. */
  onAreaIntervals: (intervals: Map<string, Interval[]> | null) => void;
  /** Of the map itself, in pixels (default 380). */
  height?: number;
}) {
  const { lanes } = props;
  const gps = useMemo(
    () =>
      lanes
        .filter((l) => l.source)
        .map((l) => ({
          key: `${l.library_id}/${l.source}`,
          name: l.source ?? "",
          files: l.files.filter(
            (f) =>
              f.kind === "gps" &&
              (f.state === "ready" || f.state === "partially_ready"),
          ),
        }))
        .filter((l) => l.files.length > 0),
    [lanes],
  );
  if (gps.length === 0) return null;
  return <MapPanel gps={gps} {...props} />;
}

/** West, south, east, north. */
export type Area = Bounds;

interface GpsLane {
  key: string;
  name: string;
  files: TimelineLane["files"];
}

/** Mounted only with tracks to show, so the map is created with its container. */
function MapPanel({
  gps,
  playhead,
  onSeek,
  span,
  area,
  onArea,
  onAreaIntervals,
  height = 380,
}: Omit<Parameters<typeof MapView>[0], "lanes"> & { gps: readonly GpsLane[] }) {
  const dark = useTheme().palette.mode === "dark";
  const maps = useQuery({
    queryKey: ["maps"],
    queryFn: () => unwrap(api.GET("/api/maps")),
  });
  const wms = useQuery({
    queryKey: ["wms"],
    queryFn: () => unwrap(api.GET("/api/wms")),
  });
  const [overlayState, setOverlayState] = useState(loadOverlayState);
  const changeOverlays = (next: OverlayState) => {
    setOverlayState(next);
    saveOverlayState(next);
  };
  const [overlayMenu, setOverlayMenu] = useState<HTMLElement | null>(null);
  const [heat, setHeat] = useState(false);
  const fileIds = gps.flatMap((l) => l.files.map((f) => f.id));
  const tracks = useQueries({
    queries: fileIds.map((id) => ({
      queryKey: ["location", id],
      queryFn: () =>
        unwrap(
          api.GET("/api/files/{id}/location", { params: { path: { id } } }),
        ),
      staleTime: Infinity,
    })),
  });
  const loaded = tracks.map((t) => t.data);
  const loadedKey = loaded.map((t) => (t ? 1 : 0)).join("");
  const sources: SourceTrack[] = useMemo(() => {
    const byId = new Map(fileIds.map((id, i) => [id, loaded[i]]));
    return gps.map((l, i) => ({
      key: l.key,
      name: l.name,
      colour: SOURCE_COLOURS[i % SOURCE_COLOURS.length] ?? "#1f77b4",
      tracks: l.files.flatMap((f) => {
        const t = byId.get(f.id);
        return t ? [t] : [];
      }),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadedKey stands for the loaded tracks
  }, [gps, loadedKey]);
  const bounds = trackBounds(sources);

  const [chosen, setChosen] = useState<string | null>(storedMap);
  const available = maps.data ?? [];
  const mapSet =
    available.find((m) => m.id === chosen) ??
    (bounds ? available.find((m) => covers(m, bounds)) : undefined) ??
    available[0] ??
    null;

  const host = useRef<HTMLDivElement>(null);
  const map = useRef<MapLibre | null>(null);
  const [ready, setReady] = useState(0);
  const latest = useRef({ sources, playhead, onSeek });
  useEffect(() => {
    latest.current = { sources, playhead, onSeek };
  });

  useEffect(() => {
    if (!host.current) return;
    const m = new maplibregl.Map({
      container: host.current,
      style: offlineStyle(null, dark),
      center: [0, 51.5],
      zoom: 4,
      attributionControl: { compact: true },
    });
    m.addControl(
      new maplibregl.NavigationControl({ showCompass: false }),
      "top-right",
    );
    m.addControl(new maplibregl.ScaleControl({}), "bottom-left");
    const popup = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: false,
    });
    m.on("mouseenter", "places", (e) => {
      m.getCanvas().style.cursor = "pointer";
      const p = e.features?.[0]?.properties as
        | Record<string, string | number>
        | undefined;
      if (!p || !e.features?.[0]) return;
      const at = new Date(Number(p["from_ms"])).toISOString().slice(11, 19);
      const what =
        p["kind"] === "stop"
          ? `Stop ${p["label"]}: ${at} UTC, ${dwell(Number(p["to_ms"]) - Number(p["from_ms"]))}`
          : `${p["kind"] === "track_start" ? "Start" : "End"}: ${at} UTC`;
      popup.setLngLat(e.lngLat).setText(`${p["name"]} — ${what}`).addTo(m);
    });
    m.on("mouseleave", "places", () => {
      m.getCanvas().style.cursor = "";
      popup.remove();
    });
    m.on("click", "places", (e) => {
      const t = Number(e.features?.[0]?.properties?.["from_ms"]);
      if (Number.isFinite(t)) latest.current.onSeek(t);
    });
    const lines = ["sections-moving", "sections-still", "span"];
    const fixAt = (e: maplibregl.MapLayerMouseEvent) => {
      if (m.queryRenderedFeatures(e.point, { layers: ["places"] }).length > 0)
        return null;
      const key = e.features?.[0]?.properties?.["source"];
      const src = latest.current.sources.find((s) => s.key === key);
      const fix = src && nearestFix(src, e.lngLat.lng, e.lngLat.lat);
      return src && fix ? { src, fix } : null;
    };
    m.on("mousemove", lines, (e) => {
      const hit = fixAt(e);
      if (!hit) return;
      m.getCanvas().style.cursor = "pointer";
      const at = new Date(hit.fix.t_ms).toISOString().slice(11, 19);
      popup
        .setLngLat([hit.fix.lon, hit.fix.lat])
        .setText(`${hit.src.name} — ${at} UTC`)
        .addTo(m);
    });
    m.on("mouseleave", lines, () => {
      m.getCanvas().style.cursor = "";
      popup.remove();
    });
    m.on("click", lines, (e) => {
      const hit = fixAt(e);
      if (hit) latest.current.onSeek(hit.fix.t_ms);
    });
    m.on("style.load", () => {
      addOverlays(m);
      setReady((n) => n + 1);
    });
    map.current = m;
    return () => {
      popup.remove();
      m.remove();
      map.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- created once; style changes go through setStyle
  }, []);

  useEffect(() => {
    map.current?.setStyle(offlineStyle(mapSet, dark));
  }, [mapSet, dark]);

  useEffect(() => {
    const m = map.current;
    if (!m || ready === 0 || !wms.data) return;
    applyOverlays(m, wms.data, overlayState, "sections-still");
  }, [wms.data, overlayState, ready]);

  useEffect(() => {
    const m = map.current;
    if (!m || ready === 0) return;
    (m.getSource("sections") as GeoJSONSource | undefined)?.setData(
      sectionFeatures(sources),
    );
    (m.getSource("places") as GeoJSONSource | undefined)?.setData(
      placeFeatures(sources),
    );
    (m.getSource("heat") as GeoJSONSource | undefined)?.setData(
      heatFeatures(sources),
    );
    (m.getSource("waypoints") as GeoJSONSource | undefined)?.setData(
      waypointFeatures(sources),
    );
  }, [sources, ready]);

  useEffect(() => {
    const m = map.current;
    if (!m || ready === 0 || !m.getLayer("heat")) return;
    m.setLayoutProperty("heat", "visibility", heat ? "visible" : "none");
  }, [heat, ready]);

  // Unchanged positions aren't sent: each map redraw is a page update, which
  // costs video frames on weak GPUs (design §7.3).
  const shownPositions = useRef("");
  useEffect(() => {
    const m = map.current;
    if (!m || ready === 0) return;
    const data =
      playhead === null ? EMPTY : positionFeatures(sources, playhead);
    const json = `${ready}:${JSON.stringify(data)}`;
    if (json === shownPositions.current) return;
    shownPositions.current = json;
    (m.getSource("positions") as GeoJSONSource | undefined)?.setData(data);
  }, [sources, playhead, ready]);

  useEffect(() => {
    const m = map.current;
    if (!m || ready === 0) return;
    (m.getSource("span") as GeoJSONSource | undefined)?.setData(
      span ? spanFeatures(sources, span.from, span.to) : EMPTY,
    );
    m.setPaintProperty("sections-moving", "line-opacity", span ? 0.35 : 1);
    m.setPaintProperty("sections-still", "line-opacity", span ? 0.2 : 0.5);
  }, [sources, span, ready]);

  useEffect(() => {
    const m = map.current;
    if (!m || ready === 0) return;
    (m.getSource("area") as GeoJSONSource | undefined)?.setData(
      area
        ? {
            type: "Feature",
            properties: {},
            geometry: {
              type: "Polygon",
              coordinates: [
                [
                  [area[0], area[1]],
                  [area[2], area[1]],
                  [area[2], area[3]],
                  [area[0], area[3]],
                  [area[0], area[1]],
                ],
              ],
            },
          }
        : EMPTY,
    );
  }, [area, ready]);

  const intervals = useMemo(
    () => (area ? insideIntervals(sources, area) : null),
    [sources, area],
  );
  useEffect(() => onAreaIntervals(intervals), [intervals, onAreaIntervals]);

  const [selecting, setSelecting] = useState(false);
  const [drawn, setDrawn] = useState<{
    x0: number;
    y0: number;
    x1: number;
    y1: number;
  } | null>(null);
  useEffect(() => {
    const m = map.current;
    if (!m) return;
    if (selecting) m.dragPan.disable();
    else m.dragPan.enable();
  }, [selecting]);
  const point = (e: React.PointerEvent) => {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const boundsKey = bounds?.join(",") ?? "";
  // Two levels short of the base map's detail, so a low-zoom map still shows
  // its land and places around a short track.
  const fitZoom = mapSet ? Math.max(0, mapSet.max_zoom - 2) : 16;
  useEffect(() => {
    const m = map.current;
    if (!m || !bounds) return;
    m.fitBounds(bounds, { padding: 40, maxZoom: fitZoom, duration: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refit when the tracks' extent or the map's detail changes
  }, [boundsKey, fitZoom]);

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2 }}
      component="section"
      aria-label="Location map"
    >
      <Stack
        direction="row"
        sx={{ alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}
      >
        <Typography variant="subtitle1" sx={{ mr: 1 }}>
          Map
        </Typography>
        {sources.map((s) => (
          <Stack
            key={s.key}
            direction="row"
            sx={{ alignItems: "center", gap: 0.5 }}
          >
            <Box
              sx={{ width: 14, height: 4, borderRadius: 1, bgcolor: s.colour }}
            />
            <Typography variant="body2">{s.name}</Typography>
          </Stack>
        ))}
        <Box sx={{ flexGrow: 1 }} />
        {area ? (
          <Button size="small" onClick={() => onArea(null)}>
            Clear area
          </Button>
        ) : (
          <Button
            size="small"
            variant={selecting ? "contained" : "text"}
            onClick={() => setSelecting(!selecting)}
          >
            {selecting ? "Drag a box on the map" : "Select area"}
          </Button>
        )}
        <Button
          size="small"
          variant={heat ? "contained" : "text"}
          onClick={() => setHeat(!heat)}
          aria-pressed={heat}
        >
          Heat map
        </Button>
        {(wms.data?.length ?? 0) > 0 && (
          <Button size="small" onClick={(e) => setOverlayMenu(e.currentTarget)}>
            Overlays
          </Button>
        )}
        {available.length > 1 && (
          <Select
            size="small"
            value={mapSet?.id ?? ""}
            onChange={(e) => {
              setChosen(e.target.value);
              try {
                localStorage.setItem(MAP_KEY, e.target.value);
              } catch {
                // Remembering the base map is a convenience.
              }
            }}
            inputProps={{ "aria-label": "Base map" }}
          >
            {available.map((m) => (
              <MenuItem key={m.id} value={m.id}>
                {m.name}
              </MenuItem>
            ))}
          </Select>
        )}
      </Stack>
      <Popover
        open={!!overlayMenu}
        anchorEl={overlayMenu}
        onClose={() => setOverlayMenu(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Stack sx={{ p: 2, gap: 1, minWidth: 280 }} aria-label="Overlays">
          <Typography variant="caption" color="text.secondary">
            Third-party layers, top first. They come through the AVGV server.
          </Typography>
          {(() => {
            const list = ordered(wms.data ?? [], overlayState);
            const ids = list.map((o) => o.id);
            return [...list].reverse().map((o) => (
              <Stack key={o.id} sx={{ gap: 0.5 }}>
                <Stack direction="row" sx={{ alignItems: "center" }}>
                  <Checkbox
                    size="small"
                    checked={!!overlayState.shown[o.id]}
                    onChange={(e) =>
                      changeOverlays({
                        ...overlayState,
                        order: ids,
                        shown: {
                          ...overlayState.shown,
                          [o.id]: e.target.checked,
                        },
                      })
                    }
                    slotProps={{ input: { "aria-label": `Show ${o.name}` } }}
                  />
                  <Typography variant="body2" sx={{ flexGrow: 1 }}>
                    {o.name}
                  </Typography>
                  <Button
                    size="small"
                    sx={{ minWidth: 32 }}
                    aria-label={`Move ${o.name} up`}
                    onClick={() =>
                      changeOverlays(move(overlayState, ids, o.id, true))
                    }
                  >
                    ↑
                  </Button>
                  <Button
                    size="small"
                    sx={{ minWidth: 32 }}
                    aria-label={`Move ${o.name} down`}
                    onClick={() =>
                      changeOverlays(move(overlayState, ids, o.id, false))
                    }
                  >
                    ↓
                  </Button>
                </Stack>
                <Slider
                  size="small"
                  min={0.1}
                  max={1}
                  step={0.1}
                  value={overlayState.opacity[o.id] ?? 0.7}
                  onChange={(_, v) =>
                    changeOverlays({
                      ...overlayState,
                      order: ids,
                      opacity: { ...overlayState.opacity, [o.id]: v as number },
                    })
                  }
                  slotProps={{
                    input: { "aria-label": `Opacity of ${o.name}` },
                  }}
                />
              </Stack>
            ));
          })()}
        </Stack>
      </Popover>
      {maps.isSuccess && available.length === 0 && (
        <Alert severity="info" sx={{ mb: 1 }}>
          No base map has been imported, so tracks are shown without one. An
          administrator can add one under Maps.
        </Alert>
      )}
      <Box sx={{ position: "relative" }}>
        <Box
          ref={host}
          sx={{
            height,
            borderRadius: 1,
            overflow: "hidden",
            [COARSE]: {
              "& .maplibregl-ctrl-group button": { width: 44, height: 44 },
            },
          }}
        />
        {selecting && (
          <Box
            aria-label="Area selection"
            sx={{
              position: "absolute",
              inset: 0,
              cursor: "crosshair",
              zIndex: 2,
              touchAction: "none",
            }}
            onPointerDown={(e) => {
              const p = point(e);
              e.currentTarget.setPointerCapture(e.pointerId);
              setDrawn({ x0: p.x, y0: p.y, x1: p.x, y1: p.y });
            }}
            onPointerMove={(e) => {
              if (!drawn) return;
              const p = point(e);
              setDrawn({ ...drawn, x1: p.x, y1: p.y });
            }}
            onPointerUp={() => {
              const m = map.current;
              setSelecting(false);
              setDrawn(null);
              if (
                !m ||
                !drawn ||
                Math.abs(drawn.x1 - drawn.x0) < 5 ||
                Math.abs(drawn.y1 - drawn.y0) < 5
              )
                return;
              const a = m.unproject([
                Math.min(drawn.x0, drawn.x1),
                Math.max(drawn.y0, drawn.y1),
              ]);
              const b = m.unproject([
                Math.max(drawn.x0, drawn.x1),
                Math.min(drawn.y0, drawn.y1),
              ]);
              onArea([a.lng, a.lat, b.lng, b.lat]);
            }}
          >
            {drawn && (
              <Box
                sx={{
                  position: "absolute",
                  border: "2px solid #ff9800",
                  bgcolor: "rgba(255, 152, 0, 0.12)",
                  left: Math.min(drawn.x0, drawn.x1),
                  top: Math.min(drawn.y0, drawn.y1),
                  width: Math.abs(drawn.x1 - drawn.x0),
                  height: Math.abs(drawn.y1 - drawn.y0),
                }}
              />
            )}
          </Box>
        )}
      </Box>
    </Paper>
  );
}
